<script setup lang="ts">
import { ref, computed, defineAsyncComponent, onMounted, watchEffect } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'
import { ElMessage } from 'element-plus'
import {
  Document,
  Grid,
  User,
  DataAnalysis,
  UserFilled,
  SwitchButton,
  Menu as MenuIcon,
  Setting,
  Close,
  Coin,
  Tickets,
  Money,
  ArrowRight,
  Download,
  Monitor,
  Bell
} from '@element-plus/icons-vue'
import SearchInput from '@/components/SearchInput.vue'
import { BYPASS_MODE } from '@/config/demoRole'
import { useVersionStore } from '@/stores/version'
import { api } from '@/services/api'
import { registraNomiTipi } from '@/config/labels'
import { iniziali } from '@/utils/format'

// Load-bearing for RNF-2 (demo code must not ship in production bundles):
// this MUST stay a conditional defineAsyncComponent, never a static import.
// BYPASS_MODE is a build-time constant, so when it's false this whole branch
// is dead code the bundler drops, and the component's chunk is never fetched.
// A static `import DemoRoleSwitch from ...` here would put it in every build.
const DemoRoleSwitch = BYPASS_MODE
  ? defineAsyncComponent(() => import('@/components/DemoRoleSwitch.vue'))
  : null

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { canAccess } = usePermissions()
const versionStore = useVersionStore()

// Una volta per sessione: un processo gia' avviato non cambia versione.
onMounted(() => versionStore.load())

// I nomi dei tipi di risorsa, per le etichette dei codici che la mappa del
// frontend non conosce (vedi `registraNomiTipi`). Senza, restano i codici.
onMounted(async () => {
  try {
    registraNomiTipi(await api.get<{ resource_type_code: string; name?: string | null }[]>('/resource-types'))
  } catch {
    // Le etichette ricadono sul codice: niente da segnalare.
  }
})

// Lo stesso codice gira in sviluppo e in produzione: lo dice il backend, e
// fuori dalla produzione lo si vede in ogni pagina e nel titolo della scheda.
// Finche' /versions non risponde l'ambiente non si sa, e non si dice niente.
const inSviluppo = computed(() => versionStore.own?.environment === 'development')
const PREFISSO_DEV = '[DEV] '
watchEffect(() => {
  const base = document.title.replace(PREFISSO_DEV, '')
  document.title = inSviluppo.value ? PREFISSO_DEV + base : base
})

import { UserRole } from '@/types'

const isCollapse = ref(false)
const isMobileMenuOpen = ref(false)
const searchQuery = ref('')

/** Una sottovoce: eredita icona e permesso dal padre, e puo' dire di se' che
 *  non e' ancora pronta. */
interface MenuChild {
  path: string
  title: string
  hint?: string
}

const menuItems: Array<{
  path: string
  icon: any
  title: string
  feature: string | null
  children?: MenuChild[]
}> = [
  { path: '/', icon: Grid, title: 'Dashboard', feature: null },
  { path: '/novita', icon: Bell, title: 'Novità', feature: 'resources' },
  // Prima dei servizi: il contratto e' la vendita, il servizio nasce da quella.
  { path: '/contracts', icon: Tickets, title: 'Contratti', feature: 'contracts' },
  { path: '/services', icon: Document, title: 'Servizi', feature: 'services' },
  { path: '/assets', icon: Grid, title: 'Asset', feature: 'services' },
  // Prima le istanze: la macchina (o il cliente), poi le sue metriche.
  { path: '/instances', icon: Monitor, title: 'Istanze', feature: 'resources' },
  { path: '/resources', icon: Coin, title: 'Risorse', feature: 'resources' },
  { path: '/resource-types', icon: Grid, title: 'Tipi di risorsa', feature: 'resource-types' },
  { path: '/raccolta-dati', icon: Download, title: 'Raccolta dati', feature: 'apitool' },
  { path: '/templates', icon: Document, title: 'Template', feature: 'templates' },
  { path: '/accounts', icon: User, title: 'Account', feature: 'customers' },
  // Dopo gli account: la fatturazione parla di societa', non di macchine.
  {
    path: '/billing',
    icon: Money,
    title: 'Fatturazione',
    feature: 'billing',
    children: [
      { path: '/billing/infragruppo', title: 'Infragruppo' },
      { path: '/billing/clienti', title: 'Clienti' },
      // Plurale: la pagina tiene tutti i listini, il selettore e' nella sua
      // intestazione. "Listino infragruppo" era vero quando ce n'era uno.
      { path: '/billing/listino', title: 'Listini' },
      { path: '/billing/cataloghi', title: 'Cataloghi' }
    ]
  },
  // Admin Panel section - requires admin role
  { path: '/admin', icon: UserFilled, title: 'Gestione utenti', feature: 'users' },
  { path: '/audit-logs', icon: Document, title: 'Log di audit', feature: 'users' },
  { path: '/sessions', icon: DataAnalysis, title: 'Sessioni', feature: 'users' },
  { path: '/roles', icon: UserFilled, title: 'Ruoli', feature: 'users' },
  { path: '/preferences', icon: Setting, title: 'Preferenze', feature: null },
]

const isUnauthorizedUser = computed(() => {
  return authStore.user && (authStore.user.role === UserRole.UNAUTHORIZED || (authStore.user.role as string) === 'unauthorized')
})

const visibleMenuItems = computed(() => {
  if (!authStore.user || isUnauthorizedUser.value) {
    return []
  }
  return menuItems.filter(item => {
    if (!item.feature) return true
    return canAccess(item.feature as any)
  })
})

/**
 * Una voce e' accesa anche sulle sue sottopagine. L'uguaglianza esatta lasciava
 * la sidebar muta ovunque si scendesse di un livello — su /billing/listino come
 * sul dettaglio di un servizio: si era dentro qualcosa e il menu non diceva
 * dentro cosa. Il confronto e' per segmento, cosi' /billing non si accende su
 * un ipotetico /billing-vecchio.
 */
const isActive = (path: string): boolean =>
  route.path === path || route.path.startsWith(path + '/')

/** Il gruppo aperto a mano. Quello in cui ci si trova e' aperto comunque. */
const gruppoAperto = ref('')

const gruppoEspanso = (path: string): boolean =>
  gruppoAperto.value === path || isActive(path)

/**
 * Da compressa il pannello delle sottovoci non ha dove stare, quindi la voce
 * padre porta dritta alla prima: e' quella su cui si lavora ogni giorno.
 */
const apriGruppo = (item: { path: string; children?: MenuChild[] }) => {
  if (isCollapse.value && item.children?.length) {
    navigateTo(item.children[0].path)
    return
  }
  gruppoAperto.value = gruppoEspanso(item.path) ? '' : item.path
}

const userInitials = computed(() => authStore.user ? iniziali(authStore.user.name || 'User') : '')

const toggleSidebar = () => {
  isCollapse.value = !isCollapse.value
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

/**
 * La ricerca dell'header va sempre ai servizi, da qualunque pagina. Prima
 * cercava nella pagina elenco aperta, che ha gia' la sua ricerca nei filtri:
 * due campi uguali a schermo, e un campo che cambiava significato a seconda
 * di dove ci si trovava.
 */
const handleSearch = () => {
  if (searchQuery.value.trim()) {
    router.push({ path: '/services', query: { q: searchQuery.value } })
    searchQuery.value = ''
    closeMobileMenu()
  }
}

const handleLogout = async () => {
  try {
    sessionStorage.removeItem('oauth_redirect')
    sessionStorage.removeItem('oauth_state')
    await authStore.logout()
    ElMessage.success('Disconnessione effettuata')
    window.location.href = '/login'
  } catch (error) {
    console.error('Logout error:', error)
    ElMessage.error('Disconnessione non riuscita. Riprova.')
  }
}

const navigateTo = (path: string) => {
  router.push(path)
  closeMobileMenu()
}
</script>

<template>
  <div class="main-layout">
    <!-- Mobile Overlay -->
    <div
      v-if="isMobileMenuOpen"
      class="mobile-overlay"
      @click="closeMobileMenu"
    />

    <!-- Sidebar -->
    <aside
      class="sidebar"
      :class="{ collapsed: isCollapse, 'mobile-open': isMobileMenuOpen }"
    >
      <!-- Logo -->
      <div class="sidebar-header">
        <div class="logo">
          <img
            class="logo-icon"
            src="/logo-36.svg"
            alt=""
          >
          <span
            v-if="!isCollapse"
            class="logo-text"
          >Service Manager</span>
        </div>
        <button
          class="close-mobile"
          @click="closeMobileMenu"
        >
          <el-icon><Close /></el-icon>
        </button>
      </div>

      <!-- Navigation -->
      <nav class="sidebar-nav">
        <template
          v-for="item in visibleMenuItems"
          :key="item.path"
        >
          <router-link
            v-if="!item.children"
            :to="item.path"
            class="nav-item"
            :class="{ active: isActive(item.path) }"
            @click="navigateTo(item.path)"
          >
            <el-icon class="nav-icon">
              <component :is="item.icon" />
            </el-icon>
            <span
              v-if="!isCollapse"
              class="nav-title"
            >{{ item.title }}</span>
          </router-link>

          <template v-else>
            <button
              type="button"
              class="nav-item nav-group"
              :class="{ active: isActive(item.path) }"
              :aria-expanded="gruppoEspanso(item.path)"
              @click="apriGruppo(item)"
            >
              <el-icon class="nav-icon">
                <component :is="item.icon" />
              </el-icon>
              <template v-if="!isCollapse">
                <span class="nav-title">{{ item.title }}</span>
                <el-icon
                  class="nav-chevron"
                  :class="{ aperto: gruppoEspanso(item.path) }"
                >
                  <ArrowRight />
                </el-icon>
              </template>
            </button>

            <div
              v-if="!isCollapse && gruppoEspanso(item.path)"
              class="nav-children"
            >
              <router-link
                v-for="child in item.children"
                :key="child.path"
                :to="child.path"
                class="nav-item nav-child"
                :class="{ active: route.path === child.path }"
                @click="navigateTo(child.path)"
              >
                <span class="nav-title">{{ child.title }}</span>
                <span
                  v-if="child.hint"
                  class="nav-hint"
                >{{ child.hint }}</span>
              </router-link>
            </div>
          </template>
        </template>
      </nav>

      <!-- Collapse Toggle -->
      <div class="sidebar-footer">
        <DemoRoleSwitch v-if="BYPASS_MODE && !isCollapse" />
        <!-- Versione del backend che sta rispondendo, non quella del bundle:
             e' la differenza fra le due che si vuole poter vedere. -->
        <router-link
          v-if="!isCollapse && versionStore.own"
          class="version-line"
          :to="{ name: 'Preferences' }"
          :title="`ServiceManager ${versionStore.own.version} — commit ${versionStore.own.commit} del ${versionStore.own.commit_date}`"
        >
          v{{ versionStore.own.version }} · {{ versionStore.own.commit }}
        </router-link>
        <button
          class="footer-button"
          title="Comprimi il menu"
          @click="toggleSidebar"
        >
          <el-icon>
            <MenuIcon />
          </el-icon>
        </button>
      </div>
    </aside>

    <!-- Main Content -->
    <div class="main-container">
      <div
        v-if="inSviluppo"
        class="ambiente"
        data-test="ambiente"
        role="status"
      >
        Server di sviluppo: i dati non sono quelli di produzione.
      </div>
      <!-- Header -->
      <header class="header">
        <div class="header-left">
          <button
            class="mobile-menu-btn"
            @click="toggleMobileMenu"
          >
            <el-icon><MenuIcon /></el-icon>
          </button>
        </div>

        <div class="header-center">
          <SearchInput
            v-model="searchQuery"
            placeholder="Cerca servizi…"
            width="100%"
            @search="handleSearch"
          />
        </div>

        <div class="header-right">
          <!-- NEW: User profile dropdown -->
          <div
            v-if="authStore.isAuthenticated"
            class="user-section"
          >
            <el-dropdown trigger="click">
              <div class="user-avatar">
                <span class="user-initials">{{ userInitials }}</span>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <div class="user-info">
                    <div class="user-name">
                      {{ authStore.user?.username }}
                    </div>
                    <div class="user-email">
                      {{ authStore.user?.email }}
                    </div>
                    <el-tag
                      size="small"
                      type="info"
                    >
                      {{ authStore.user?.role }}
                    </el-tag>
                  </div>
                  <el-dropdown-item
                    divided
                    :icon="SwitchButton"
                    @click="handleLogout"
                  >
                    Esci
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="main-content">
        <router-view />
      </main>
    </div>
  </div>
</template>

<style scoped>
.main-layout {
  display: flex;
  min-height: calc(100vh - var(--sm-banner-h));
  width: 100%;
}

/* Mobile Overlay */
.mobile-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: var(--sm-scrim);
  z-index: 40;
}

@media (max-width: 768px) {
  .mobile-overlay {
    display: block;
  }
}

/* Sidebar */
.sidebar {
  width: 240px;
  background: var(--sm-nav);
  display: flex;
  flex-direction: column;
  transition: width 0.3s, transform 0.3s;
  position: fixed;
  top: var(--sm-banner-h);
  height: calc(100vh - var(--sm-banner-h));
  z-index: 50;
}

.sidebar.collapsed {
  width: 72px;
}

@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
    width: 280px;
  }

  .sidebar.mobile-open {
    transform: translateX(0);
  }

  .sidebar.collapsed {
    width: 280px;
  }
}

/* Sidebar Header */
.sidebar-header {
  height: 64px;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--sm-nav-rule);
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.logo-text {
  color: var(--sm-nav-ink-strong);
  font-family: var(--sm-font-display);
  font-weight: 600;
  font-size: 1rem;
  white-space: nowrap;
}

.close-mobile {
  display: none;
  background: none;
  border: none;
  color: var(--sm-nav-ink);
  cursor: pointer;
  padding: 0.5rem;
}

@media (max-width: 768px) {
  .close-mobile {
    display: flex;
  }
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  padding: 1rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  color: var(--sm-nav-ink);
  font-size: var(--sm-nav-text);
  font-weight: var(--sm-nav-weight);
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  text-align: left;
  width: 100%;
  text-decoration: none;
}

.nav-item:hover {
  background: var(--sm-nav-hover);
  color: var(--sm-nav-ink-strong);
}

.nav-item.active {
  background: var(--sm-nav-active-bg);
  color: var(--sm-nav-active-ink);
  font-weight: var(--sm-nav-active-weight);
  border-left: 3px solid var(--sm-nav-active-bar);
  margin-left: -3px;
}

.nav-item.active .nav-icon {
  color: var(--sm-nav-active-ink);
}

.nav-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.nav-title {
  white-space: nowrap;
}

.nav-group .nav-title {
  flex: 1;
}

.nav-chevron {
  font-size: 0.9rem;
  transition: transform 0.2s;
}

.nav-chevron.aperto {
  transform: rotate(90deg);
}

/* Le sottovoci stanno sotto l'icona del padre, allineate al suo titolo. */
.nav-children {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-left: 2rem;
}

.nav-child {
  padding: 0.5rem 0.75rem;
  font-size: var(--sm-text-sm);
}

/* Una sottovoce che non e' ancora pronta lo dice qui, non in una pagina che
   l'utente scopre solo dopo averci cliccato. */
.nav-hint {
  margin-left: auto;
  padding: 0.05rem 0.35rem;
  border-radius: var(--sm-radius-pill);
  background: var(--sm-nav-hover);
  font-size: var(--sm-text-xs);
  opacity: 0.75;
}

.sidebar.collapsed .nav-item {
  justify-content: center;
  padding: 0.75rem;
}

/* Sidebar Footer */
.sidebar-footer {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sm-space-sm);
  padding: var(--sm-space-md);
  border-top: 1px solid var(--sm-nav-rule);
}

/* Riga intera: il commit accanto alla versione non sta in mezza sidebar. */
.version-line {
  flex: 1 0 100%;
  font-family: var(--sm-font-mono, monospace);
  font-size: var(--sm-text-xs);
  color: var(--sm-nav-ink);
  opacity: 0.55;
  text-decoration: none;
  text-align: center;
  letter-spacing: 0.02em;
}

.version-line:hover {
  opacity: 1;
}

.footer-button {
  flex: 1;
  min-width: 0;
  gap: var(--sm-space-sm);
  padding: 0.75rem;
  font-family: var(--sm-font-ui);
  font-size: var(--sm-text-xs);
  border: 1px solid var(--sm-nav-rule);
  border-radius: var(--sm-radius-sm);
  background: transparent;
  color: var(--sm-nav-ink);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.footer-button:hover {
  background: var(--sm-nav-hover);
  color: var(--sm-nav-ink-strong);
}

@media (max-width: 768px) {
  .sidebar-footer {
    display: none;
  }
}

/* Main Container */
/* Una riga sola e quieta: si deve notare, non deve spostare l'attenzione dal
   lavoro. Stessi toni dell'avviso usati nel resto dell'app. */
.ambiente {
  padding: 4px 24px;
  font-size: var(--sm-text-xs);
  color: var(--sm-warn-strong);
  background: var(--sm-warn-soft);
  border-bottom: 1px solid var(--sm-warn-line);
}

.main-container {
  flex: 1;
  /* Same reason as .main-content below: without this a wide table stretches
     the column past the window instead of scrolling inside it. */
  min-width: 0;
  margin-left: 240px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - var(--sm-banner-h));
  transition: margin-left 0.3s;
}

.sidebar.collapsed ~ .main-container {
  margin-left: 72px;
}

@media (max-width: 768px) {
  .main-container {
    margin-left: 0;
  }

  .sidebar.collapsed ~ .main-container {
    margin-left: 0;
  }
}

/* Header */
.header {
  height: 64px;
  background: var(--sm-surface);
  border-bottom: 1px solid var(--sm-rule);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  position: sticky;
  top: var(--sm-banner-h);
  z-index: 30;
}

.header-left {
  display: flex;
  align-items: center;
}

.mobile-menu-btn {
  display: none;
  padding: 0.5rem;
  border: none;
  background: transparent;
  color: var(--sm-muted);
  cursor: pointer;
  font-size: 1.25rem;
}

@media (max-width: 768px) {
  .mobile-menu-btn {
    display: flex;
  }
}

.header-center {
  flex: 1;
  max-width: 400px;
  margin: 0 1.5rem;
}





@media (max-width: 768px) {
  .header-center {
    display: none;
  }
}

.header-right {
  display: flex;
  align-items: center;
}

/* User Section */
.user-section {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--sm-avatar-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s;
}

.user-avatar:hover {
  transform: scale(1.05);
}

.user-initials {
  color: var(--sm-nav-ink-strong);
  font-weight: 600;
  font-size: 0.9rem;
}

.user-info {
  padding: 0.75rem 1rem;
  min-width: 200px;
}

.user-name {
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: var(--sm-ink);
}

.user-email {
  font-size: 0.85rem;
  color: var(--sm-muted);
  margin-bottom: 0.5rem;
}

/* Main Content */
.main-content {
  flex: 1;
  /* A flex item defaults to min-width: auto, so a wide table used to stretch
     this past the window and scroll the whole page sideways — header, sidebar
     and all. With min-width: 0 it shrinks to the window and wide content
     scrolls inside its own container instead. */
  min-width: 0;
  padding: 1.5rem;
  background: var(--sm-bg);
  overflow-y: auto;
}

@media (max-width: 768px) {
  .main-content {
    padding: 1rem;
  }
}
</style>
